import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';

import { toast } from 'react-toastify';
import { logger } from 'RootShared/utility/ErrorLogger';

import Dashboard from '../../components/Dashboard';
import TableTree from 'Components/Admin/TableTree';
import strToBool from 'Shared/util/strToBool';
import createPeriod from '../../utilityReporting/createPeriod';
import {
  createTreeArr,
  createTreeArrProject,
  createTreeArrCurrency,
} from '../../utilityReporting/createTreeArrCashFlows';
import { ALL_CURRENCIES, ONE_CURRENCY } from '../../constants/constants';
import {
  queryReportsPayments,
  queryReportsPaymentsByPrj,
  queryWagesSalaries,
  queryWagesSalariesByProject,
  queryWagesSalariesByEmployee,
  queryWagesSalariesAdmin,
  queryWagesSalariesAssigned,
  queryWagesSalariesNotAssigned,
  queryExpenses,
  queryExpensesByProject,
  queryExpensesByCategory,
  queryExpensesByPC,
  queryExtraIncome,
  queryExtraIncomeByCategory,
  queryInvestments,
  queryInvestmentsByCategory,
  queryDistributions,
  queryDistributionsByCategory,
  queryTaxes,
  queryTaxesByCategory,
  queryBalances,
  queryBalanceChanges,
  queryDiscrepancy,
  queryNetCashFlow,
  queryNetCashFlowByProject,
  queryExchangeOperations,
} from './query';
import useDocumentTitle from 'Shared/hooks/useDocumentTitle';
import usePreserveSearchParams from 'Shared/hooks/usePreserveSearchParams';
import useInterval from '../../hooks/useInterval';
import { extractPeriodArray } from '../../utilityReporting/treeCreationMethods';

const toastParams = {
  autoClose: 3000,
  closeButton: true,
  hideProgressBar: false,
};

const CASH_FLOW_FILTERS = 'cash-flow-filters';
const MAX_NUMBER_OF_INTERVAL = 3;

function CashFlows(props) {
  const history = useHistory();
  const controller = useRef(new AbortController());
  const [searchParams, setSearchParams] = usePreserveSearchParams(CASH_FLOW_FILTERS);

  useDocumentTitle('Reporting - Cash Flows');

  const [period, setPeriod] = useState(searchParams.get('period') || 'MONTH');
  const [currency, setCurrency] = useState(searchParams.get('currency') || 'USD');
  const [isProjectSelected, setIsProjectSelected] = useState(strToBool(searchParams.get('by-project')));
  const [render, setRender] = useState(false);
  const [dateColumnsPeriod, setDateColumnsPeriod] = useState(createPeriod(period + 's'));
  const [showPercent, setShowPercent] = useState(false);
  const doubleError = useRef([]);
  const numberOfIntervals = useRef(0);

  const handlePerception = () => {
    setShowPercent((prev) => !prev);
  };

  const displayErrorToast = (massage) =>
    toast(massage, {
      type: toast.TYPE.ERROR,
      ...toastParams,
    });

  const handleError = (doubleError, error) => {
    if (!doubleError.current.includes(error.message)) {
      displayErrorToast(error.message);
      logger.error(error);
    }
    doubleError.current = [...doubleError.current, error.message];
  };

  const queryParams = {
    fetchPolicy: 'no-cache',
    onCompleted: () => {
      !render && setRender(true);
    },
    onError({ networkError, graphQLErrors }) {
      networkError?.result?.errors &&
        networkError.result.errors.forEach((error) => {
          handleError(doubleError, error);
        });
      graphQLErrors &&
        graphQLErrors.forEach((error) => {
          handleError(doubleError, error);
        });
    },
    pollInterval: 30000,
  };

  const [getPayments, { data: dataPayments, error: errorPayments, stopPolling: stopPayments }] = useLazyQuery(
    queryReportsPayments,
    queryParams
  );
  const [
    getPaymentsByPrj,
    { data: dataPaymentsByPrj, error: errorPaymentsByPrj, stopPolling: stopPaymentsByPrj },
  ] = useLazyQuery(queryReportsPaymentsByPrj, queryParams);

  const [getSalaries, { data: dataSalaries, error: errorSalaries, stopPolling: stopSalaries }] = useLazyQuery(
    queryWagesSalaries,
    queryParams
  );
  const [
    getSalariesByProject,
    { data: dataSalariesByProject, error: errorSalariesByProject, stopPolling: stopSalariesByProject },
  ] = useLazyQuery(queryWagesSalariesByProject, queryParams);
  const [
    getSalariesByEmployee,
    { data: dataSalariesByEmployee, error: errorSalariesByEmployee, stopPolling: stopSalariesByEmployee },
  ] = useLazyQuery(queryWagesSalariesByEmployee, queryParams);
  const [
    getSalariesAdmin,
    { data: dataSalariesAdmin, error: errorSalariesAdmin, stopPolling: stopSalariesAdmin },
  ] = useLazyQuery(queryWagesSalariesAdmin, queryParams);
  const [
    getSalariesAssigned,
    { data: dataSalariesAssigned, error: errorSalariesAssigned, stopPolling: stopSalariesAssigned },
  ] = useLazyQuery(queryWagesSalariesAssigned, queryParams);
  const [
    getSalariesNotAssigned,
    { data: dataSalariesNotAssigned, error: errorSalariesNotAssigned, stopPolling: stopSalariesNotAssigned },
  ] = useLazyQuery(queryWagesSalariesNotAssigned, queryParams);

  const [getExpenses, { data: dataExpenses, error: errorExpenses, stopPolling: stopExpenses }] = useLazyQuery(
    queryExpenses,
    queryParams
  );
  const [
    getExpensesProject,
    { data: dataExpensesProject, error: errorExpensesProject, stopPolling: stopExpensesProject },
  ] = useLazyQuery(queryExpensesByProject, queryParams);
  const [
    getExpensesByCategory,
    { data: dataExpensesByCategory, error: errorExpensesByCategory, stopPolling: stopExpensesByCategory },
  ] = useLazyQuery(queryExpensesByCategory, queryParams);
  const [
    getExpensesBPC,
    { data: dataExpensesBPC, error: errorExpensesBPC, stopPolling: stopExpensesBPC },
  ] = useLazyQuery(queryExpensesByPC, queryParams);

  const [
    getExtraIncome,
    { data: dataExtraIncome, error: errorExtraIncome, stopPolling: stopExtraIncome },
  ] = useLazyQuery(queryExtraIncome, queryParams);
  const [
    getExtraIncomeByCategory,
    { data: dataExtraIncomeByCategory, error: errorExtraIncomeByCategory, stopPolling: stopExtraIncomeByCategory },
  ] = useLazyQuery(queryExtraIncomeByCategory, queryParams);

  const [
    getInvestments,
    { data: dataInvestments, error: errorInvestments, stopPolling: stopInvestments },
  ] = useLazyQuery(queryInvestments, queryParams);
  const [
    getInvestmentsByCategory,
    { data: dataInvestmentsByCategory, error: errorInvestmentsByCategory, stopPolling: stopInvestmentsByCategory },
  ] = useLazyQuery(queryInvestmentsByCategory, queryParams);

  const [
    getDistributions,
    { data: dataDistributions, error: errorDistributions, stopPolling: stopDistributions },
  ] = useLazyQuery(queryDistributions, queryParams);
  const [
    getDistributionsByCategory,
    {
      data: dataDistributionsByCategory,
      error: errorDistributionsByCategory,
      stopPolling: stopDistributionsByCategory,
    },
  ] = useLazyQuery(queryDistributionsByCategory, queryParams);

  const [getTaxes, { data: dataTaxes, error: errorTaxes, stopPolling: stopTaxes }] = useLazyQuery(
    queryTaxes,
    queryParams
  );
  const [
    getTaxesByCategory,
    { data: dataTaxesByCategory, error: errorTaxesByCategory, stopPolling: stopTaxesByCategory },
  ] = useLazyQuery(queryTaxesByCategory, queryParams);

  const [getBalances, { data: dataBalances, error: errorBalances, stopPolling: stopBalances }] = useLazyQuery(
    queryBalances,
    queryParams
  );
  const [
    getBalanceChanges,
    { data: dataBalanceChanges, error: errorBalanceChanges, stopPolling: stopBalanceChanges },
  ] = useLazyQuery(queryBalanceChanges, queryParams);
  const [
    getDiscrepancy,
    { data: dataDiscrepancy, error: errorDiscrepancy, stopPolling: stopDiscrepancy },
  ] = useLazyQuery(queryDiscrepancy, queryParams);
  const [
    getExchangeOperations,
    { data: dataExchangeOperations, error: errorExchangeOperations, stopPolling: stopPollingExchangeOperations },
  ] = useLazyQuery(queryExchangeOperations, queryParams);

  const [
    getNetCashFlow,
    { data: dataNetCashFlow, error: errorNetCashFlow, stopPolling: stopPollingNetCashFlow },
  ] = useLazyQuery(queryNetCashFlow, queryParams);

  const [
    getNetCashFlowByProject,
    { data: dataNetCashFlowByProject, error: errorNetCashFlowByProject, stopPolling: stopNetCashFlowByProject },
  ] = useLazyQuery(queryNetCashFlowByProject, queryParams);

  const stopPollingByProject = useCallback(() => {
    (dataNetCashFlowByProject || errorNetCashFlowByProject) && stopNetCashFlowByProject();
    (dataPaymentsByPrj || errorPaymentsByPrj) && stopPaymentsByPrj();
    (dataSalariesByProject || errorSalariesByProject) && stopSalariesByProject();
    (dataSalariesByEmployee || errorSalariesByEmployee) && stopSalariesByEmployee();
    (dataExpensesProject || errorExpensesProject) && stopExpensesProject();
    (dataExpensesBPC || errorExpensesBPC) && stopExpensesBPC();
  }, [
    dataExpensesBPC,
    dataExpensesProject,
    dataNetCashFlowByProject,
    dataPaymentsByPrj,
    dataSalariesByEmployee,
    dataSalariesByProject,
    errorExpensesBPC,
    errorExpensesProject,
    errorNetCashFlowByProject,
    errorPaymentsByPrj,
    errorSalariesByEmployee,
    errorSalariesByProject,
    stopExpensesBPC,
    stopExpensesProject,
    stopNetCashFlowByProject,
    stopPaymentsByPrj,
    stopSalariesByEmployee,
    stopSalariesByProject,
  ]);

  const stopPollingByCurrency = useCallback(() => {
    (dataPayments || errorPayments) && stopPayments();
    (dataPaymentsByPrj || errorPaymentsByPrj) && stopPaymentsByPrj();
    (dataSalaries || errorSalaries) && stopSalaries();
    (dataSalariesAdmin || errorSalariesAdmin) && stopSalariesAdmin();
    (dataSalariesAssigned || errorSalariesAssigned) && stopSalariesAssigned();
    (dataSalariesNotAssigned || errorSalariesNotAssigned) && stopSalariesNotAssigned();
    (dataExpenses || errorExpenses) && stopExpenses();
    (dataExpensesByCategory || errorExpensesByCategory) && stopExpensesByCategory();
    (dataExtraIncome || errorExtraIncome) && stopExtraIncome();
    (dataExtraIncomeByCategory || errorExtraIncomeByCategory) && stopExtraIncomeByCategory();
    (dataInvestments || errorInvestments) && stopInvestments();
    (dataInvestmentsByCategory || errorInvestmentsByCategory) && stopInvestmentsByCategory();
    (dataDistributions || errorDistributions) && stopDistributions();
    (dataDistributionsByCategory || errorDistributionsByCategory) && stopDistributionsByCategory();
    (dataTaxes || errorTaxes) && stopTaxes();
    (dataBalances || errorBalances) && stopBalances();
    (dataTaxesByCategory || errorTaxesByCategory) && stopTaxesByCategory();
    (dataBalanceChanges || errorBalanceChanges) && stopBalanceChanges();
    (dataDiscrepancy || errorDiscrepancy) && stopDiscrepancy();
    (dataExchangeOperations || errorExchangeOperations) && stopPollingExchangeOperations();
    (dataNetCashFlow || errorNetCashFlow) && stopPollingNetCashFlow();
  }, [
    dataBalanceChanges,
    dataBalances,
    dataDiscrepancy,
    dataDistributions,
    dataDistributionsByCategory,
    dataExchangeOperations,
    dataExpenses,
    dataExpensesByCategory,
    dataExtraIncome,
    dataExtraIncomeByCategory,
    dataInvestments,
    dataInvestmentsByCategory,
    dataNetCashFlow,
    dataPayments,
    dataPaymentsByPrj,
    dataSalaries,
    dataSalariesAdmin,
    dataSalariesAssigned,
    dataSalariesNotAssigned,
    dataTaxes,
    dataTaxesByCategory,
    errorBalanceChanges,
    errorBalances,
    errorDiscrepancy,
    errorDistributions,
    errorDistributionsByCategory,
    errorExchangeOperations,
    errorExpenses,
    errorExpensesByCategory,
    errorExtraIncome,
    errorExtraIncomeByCategory,
    errorInvestments,
    errorInvestmentsByCategory,
    errorNetCashFlow,
    errorPayments,
    errorPaymentsByPrj,
    errorSalaries,
    errorSalariesAdmin,
    errorSalariesAssigned,
    errorSalariesNotAssigned,
    errorTaxes,
    errorTaxesByCategory,
    stopBalanceChanges,
    stopBalances,
    stopDiscrepancy,
    stopDistributions,
    stopDistributionsByCategory,
    stopExpenses,
    stopExpensesByCategory,
    stopExtraIncome,
    stopExtraIncomeByCategory,
    stopInvestments,
    stopInvestmentsByCategory,
    stopPayments,
    stopPaymentsByPrj,
    stopPollingExchangeOperations,
    stopPollingNetCashFlow,
    stopSalaries,
    stopSalariesAdmin,
    stopSalariesAssigned,
    stopSalariesNotAssigned,
    stopTaxes,
    stopTaxesByCategory,
  ]);

  const [stopPolling, resetInterval] = useInterval(MAX_NUMBER_OF_INTERVAL, period, isProjectSelected);

  useEffect(() => {
    if (stopPolling) {
      stopPollingByCurrency();
      stopPollingByProject();
    }

    if (
      (dataPayments || errorPayments) &&
      (dataPaymentsByPrj || errorPaymentsByPrj) &&
      (dataSalaries || errorSalaries) &&
      (dataSalariesAdmin || errorSalariesAdmin) &&
      (dataSalariesAssigned || errorSalariesAssigned) &&
      (dataSalariesNotAssigned || errorSalariesNotAssigned) &&
      (dataExpenses || errorExpenses) &&
      (dataExpensesByCategory || errorExpensesByCategory) &&
      (dataExtraIncome || errorExtraIncome) &&
      (dataExtraIncomeByCategory || errorExtraIncomeByCategory) &&
      (dataInvestments || errorInvestments) &&
      (dataInvestmentsByCategory || errorInvestmentsByCategory) &&
      (dataDistributions || errorDistributions) &&
      (dataDistributionsByCategory || errorDistributionsByCategory) &&
      (dataTaxes || errorTaxes) &&
      (dataBalances || errorBalances) &&
      (dataTaxesByCategory || errorTaxesByCategory) &&
      (dataBalanceChanges || errorBalanceChanges) &&
      (dataDiscrepancy || errorDiscrepancy) &&
      (dataExchangeOperations || errorExchangeOperations) &&
      (dataNetCashFlow || errorNetCashFlow)
    ) {
      resetInterval();
      stopPollingByCurrency();
    }

    if (
      (dataNetCashFlowByProject || errorNetCashFlowByProject) &&
      (dataPaymentsByPrj || errorPaymentsByPrj) &&
      (dataSalariesByProject || errorSalariesByProject) &&
      (dataSalariesByEmployee || errorSalariesByEmployee) &&
      (dataExpensesProject || errorExpensesProject) &&
      (dataExpensesBPC || errorExpensesBPC)
    ) {
      resetInterval();
      stopPollingByProject();
    }
  }, [
    dataBalanceChanges,
    dataBalances,
    dataDiscrepancy,
    dataDistributions,
    dataDistributionsByCategory,
    dataExchangeOperations,
    dataExpenses,
    dataExpensesBPC,
    dataExpensesByCategory,
    dataExpensesProject,
    dataExtraIncome,
    dataExtraIncomeByCategory,
    dataInvestments,
    dataInvestmentsByCategory,
    dataNetCashFlow,
    dataNetCashFlowByProject,
    dataPayments,
    dataPaymentsByPrj,
    dataSalaries,
    dataSalariesAdmin,
    dataSalariesAssigned,
    dataSalariesByEmployee,
    dataSalariesByProject,
    dataSalariesNotAssigned,
    dataTaxes,
    dataTaxesByCategory,
    errorBalanceChanges,
    errorBalances,
    errorDiscrepancy,
    errorDistributions,
    errorDistributionsByCategory,
    errorExchangeOperations,
    errorExpenses,
    errorExpensesBPC,
    errorExpensesByCategory,
    errorExpensesProject,
    errorExtraIncome,
    errorExtraIncomeByCategory,
    errorInvestments,
    errorInvestmentsByCategory,
    errorNetCashFlow,
    errorNetCashFlowByProject,
    errorPayments,
    errorPaymentsByPrj,
    errorSalaries,
    errorSalariesAdmin,
    errorSalariesAssigned,
    errorSalariesByEmployee,
    errorSalariesByProject,
    errorSalariesNotAssigned,
    errorTaxes,
    errorTaxesByCategory,
    resetInterval,
    stopPolling,
    stopPollingByCurrency,
    stopPollingByProject,
  ]);

  const request = useCallback(
    (queryBody) => {
      getPayments(queryBody);
      getPaymentsByPrj(queryBody);
      getSalaries(queryBody);
      getSalariesAdmin(queryBody);
      getSalariesAssigned(queryBody);
      getSalariesNotAssigned(queryBody);
      getExpenses(queryBody);
      getExpensesByCategory(queryBody);
      getExtraIncome(queryBody);
      getExtraIncomeByCategory(queryBody);
      getInvestments(queryBody);
      getInvestmentsByCategory(queryBody);
      getDistributions(queryBody);
      getDistributionsByCategory(queryBody);
      getTaxes(queryBody);
      getTaxesByCategory(queryBody);
      getBalances(queryBody);
      getBalanceChanges(queryBody);
      getDiscrepancy(queryBody);
      getExchangeOperations(queryBody);
      getNetCashFlow(queryBody);
    },
    [
      getBalanceChanges,
      getBalances,
      getDiscrepancy,
      getDistributions,
      getDistributionsByCategory,
      getExchangeOperations,
      getExpenses,
      getExpensesByCategory,
      getExtraIncome,
      getExtraIncomeByCategory,
      getInvestments,
      getInvestmentsByCategory,
      getNetCashFlow,
      getPayments,
      getPaymentsByPrj,
      getSalaries,
      getSalariesAdmin,
      getSalariesAssigned,
      getSalariesNotAssigned,
      getTaxes,
      getTaxesByCategory,
    ]
  );

  const requestByProject = useCallback(
    (queryBody) => {
      getExpensesProject(queryBody);
      getSalariesByProject(queryBody);
      getPaymentsByPrj(queryBody);
      getNetCashFlowByProject(queryBody);
      getSalariesByEmployee(queryBody);
      getExpensesBPC(queryBody);
    },
    [
      getExpensesBPC,
      getExpensesProject,
      getNetCashFlowByProject,
      getPaymentsByPrj,
      getSalariesByEmployee,
      getSalariesByProject,
    ]
  );

  useEffect(() => {
    if (controller.current) {
      controller.current.abort();
    }
    controller.current = new AbortController();

    const context = {
      fetchOptions: {
        signal: controller.current.signal,
      },
      queryDeduplication: false,
    };

    if (currency === ALL_CURRENCIES) {
      request({
        variables: { freq: period },
        context,
      });
    } else if (isProjectSelected) {
      requestByProject({
        variables: { currency: currency, freq: period },
        context,
      });
    } else if (!isProjectSelected && currency !== ALL_CURRENCIES) {
      request({
        variables: { currency: currency, freq: period },
        context,
      });
    }

    doubleError.current = [];
  }, [controller, currency, isProjectSelected, period, request, requestByProject]);

  const getCreatTreeFunction = () => {
    if (!render) return [];

    if (currency === ALL_CURRENCIES) {
      return createTreeArrCurrency(
        dateColumnsPeriod,
        dataPayments?.payments,
        dataPaymentsByPrj?.payments,
        dataSalaries?.salaries,
        dataSalariesAdmin?.salaries,
        dataSalariesAssigned?.salaries,
        dataSalariesNotAssigned?.salaries,
        dataExpenses?.expenses,
        dataExpensesByCategory?.expenses,
        dataExtraIncome?.extraIncome,
        dataExtraIncomeByCategory?.extraIncome,
        dataInvestments?.expenses,
        dataInvestmentsByCategory?.expenses,
        dataDistributions?.expenses,
        dataDistributionsByCategory?.expenses,
        dataTaxes?.expenses,
        dataTaxesByCategory?.expenses,
        dataBalances?.balances,
        dataBalanceChanges?.balanceChanges,
        dataDiscrepancy?.discrepancy,
        dataExchangeOperations?.exchangeTransactions,
        dataNetCashFlow?.cashFlow,
        period
      );
    } else if (isProjectSelected) {
      return createTreeArrProject(
        dateColumnsPeriod,
        dataNetCashFlowByProject?.cashFlow,
        dataPaymentsByPrj?.payments,
        dataSalariesByProject?.salaries,
        dataSalariesByEmployee?.salaries,
        dataExpensesProject?.expenses,
        dataExpensesBPC?.expenses,
        period
      );
    } else if (!isProjectSelected && currency !== ALL_CURRENCIES) {
      return createTreeArr(
        dateColumnsPeriod,
        dataPayments?.payments,
        dataPaymentsByPrj?.payments,
        dataSalaries?.salaries,
        dataSalariesAdmin?.salaries,
        dataSalariesAssigned?.salaries,
        dataSalariesNotAssigned?.salaries,
        dataExpenses?.expenses,
        dataExpensesByCategory?.expenses,
        dataExtraIncome?.extraIncome,
        dataExtraIncomeByCategory?.extraIncome,
        dataInvestments?.expenses,
        dataInvestmentsByCategory?.expenses,
        dataDistributions?.expenses,
        dataDistributionsByCategory?.expenses,
        dataTaxes?.expenses,
        dataTaxesByCategory?.expenses,
        dataBalances?.balances,
        dataBalanceChanges?.balanceChanges,
        dataDiscrepancy?.discrepancy,
        dataExchangeOperations?.exchangeTransactions,
        dataNetCashFlow?.cashFlow,
        currency,
        ONE_CURRENCY,
        period
      );
    }
  };
  const tree = getCreatTreeFunction();
  return (
    <div>
      <Dashboard
        handlePerception={handlePerception}
        localStorageItem={CASH_FLOW_FILTERS}
        showPercent={showPercent}
        getCurrency={({ currencyLabel, projectSelected }) => {
          const periodLabel = period + 's';
          const searchParams = new URLSearchParams('');
          let currencyParam = '';
          numberOfIntervals.current = 0;
          setDateColumnsPeriod(createPeriod(periodLabel));

          if (currencyLabel === ALL_CURRENCIES) {
            setCurrency(ALL_CURRENCIES);
            setIsProjectSelected(projectSelected);
            currencyParam = ALL_CURRENCIES;
            currency !== currencyLabel && setRender(false);
          } else {
            const curr = currencyLabel.match(/\((.*)\)/).pop();

            if (currency !== curr || projectSelected !== isProjectSelected) {
              setRender(false);
            }

            setCurrency(curr);
            setIsProjectSelected(projectSelected);
            currencyParam = curr;
          }

          searchParams.append('period', period);
          searchParams.append('currency', currencyParam);
          searchParams.append('by-project', projectSelected);

          if (searchParams.toString()) {
            setSearchParams(CASH_FLOW_FILTERS, searchParams.toString());
            history.push(`?${searchParams.toString()}`);
          }
        }}
        getPeriod={(periodLabel) => {
          const periodForQuery = periodLabel.toUpperCase().slice(0, -1);
          const searchParams = new URLSearchParams('');
          numberOfIntervals.current = 0;
          setPeriod(periodForQuery);
          setDateColumnsPeriod(createPeriod(periodLabel));
          period !== periodForQuery && setRender(false);

          searchParams.append('period', periodForQuery);
          searchParams.append('currency', currency);
          searchParams.append('by-project', isProjectSelected);

          if (searchParams.toString()) {
            setSearchParams(CASH_FLOW_FILTERS, searchParams.toString());
            history.push(`?${searchParams.toString()}`);
          }
        }}
      />
      <TableTree
        treeArr={tree}
        dateColumnsPeriod={tree.length > 0 && render ? extractPeriodArray(tree) : []}
        showPercent={showPercent}
        {...props}
      />
    </div>
  );
}

export default CashFlows;
